import { Wrench, Calendar } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { OrderData } from './types';

interface OrderViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: OrderData;
  conversationHtml: string;
}

export function OrderViewDialog({
  open,
  onOpenChange,
  order,
  conversationHtml
}: OrderViewDialogProps) {
  const getStatusBadge = () => {
    const statusStyles: Record<string, string> = {
      pending: 'bg-amber-100 text-amber-800',
      accepted: 'bg-emerald-100 text-emerald-800',
      rejected: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800'
    };
    const statusLabels: Record<string, string> = {
      pending: 'Väntar',
      accepted: 'Accepterad',
      rejected: 'Avvisad',
      completed: 'Slutförd'
    };
    return (
      <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${statusStyles[order.status || ''] || 'bg-slate-100 text-slate-800'}`}>
        {statusLabels[order.status || ''] || order.status}
      </span>
    );
  };

  const getStatusIndicator = () => {
    const statusColors: Record<string, string> = {
      pending: 'bg-amber-500',
      accepted: 'bg-emerald-500',
      rejected: 'bg-red-500',
      completed: 'bg-blue-500'
    };
    const statusTitles: Record<string, string> = {
      pending: 'Väntar på svar',
      accepted: 'Accepterad',
      rejected: 'Avvisad',
      completed: 'Slutförd'
    };
    return (
      <span 
        className={`ml-1 w-2 h-2 rounded-full ${statusColors[order.status || ''] || ''}`} 
        title={statusTitles[order.status || '']}
      />
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-7 px-1.5 sm:px-2 text-xs"
          data-testid="view-order-button"
        >
          <Wrench size={14} className="sm:mr-1" />
          <span className="hidden sm:inline">Öppna arbetsorder</span>
          {getStatusIndicator()}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Wrench size={18} />
            Arbetsorder
            {getStatusBadge()}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          {/* Subcontractor info */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">Leverantör</div>
            <div className="font-medium text-slate-900">{order.subcontractor_name}</div>
            <div className="text-sm text-slate-600">{order.subcontractor_email}</div>
            {order.subcontractor_phone && (
              <div className="text-sm text-slate-600">{order.subcontractor_phone}</div>
            )}
          </div>

          {/* Due date */}
          {order.due_date && (
            <div className="flex items-center gap-2 text-sm">
              <Calendar size={14} className="text-slate-400" />
              <span className="text-slate-600">Önskat slutdatum:</span>
              <span className="font-medium">{new Date(order.due_date).toLocaleDateString('sv-SE')}</span>
            </div>
          )}

          {/* Response from subcontractor */}
          {order.response && (
            <div className={`rounded-lg p-4 ${
              order.status === 'rejected' ? 'bg-red-50 border border-red-200' : 'bg-emerald-50 border border-emerald-200'
            }`}>
              <div className={`text-xs font-medium uppercase tracking-wide mb-2 ${
                order.status === 'rejected' ? 'text-red-700' : 'text-emerald-700'
              }`}>
                Svar från leverantör
              </div>
              <p className="text-sm whitespace-pre-wrap">{order.response}</p>
              {order.responded_at && (
                <div className="text-xs text-slate-500 mt-2">
                  {new Date(order.responded_at).toLocaleString('sv-SE')}
                </div>
              )}
            </div>
          )}

          {/* Live conversation - always shows current state */}
          <div className="space-y-2">
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">Ärendehistorik</div>
            <div 
              className="border border-slate-200 rounded-lg overflow-hidden bg-white max-h-[300px] overflow-y-auto"
              dangerouslySetInnerHTML={{ __html: conversationHtml }}
            />
          </div>

          {/* Timestamps */}
          <div className="flex items-center justify-between text-[10px] text-slate-400 pt-2 border-t border-slate-100">
            <span>Skapad: {order.created_at ? new Date(order.created_at).toLocaleString('sv-SE') : '-'}</span>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <a 
              href={`${window.location.origin}/orders/confirm/${order.access_token}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1"
            >
              <Button variant="outline" size="sm" className="w-full text-xs">
                Öppna extern sida
              </Button>
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
